import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useToast } from 'providers/ToastProvider';
import { useAdicionarItemComProduto } from '../../../../hooks/useNFe';
import { BuscarProdutosNFeItem } from '../../../../types/nfe';
import ModalBuscarProdutoNFe from './ModalBuscarProdutoNFe';

interface BarraAdicionarItemProps {
  nfeId: string;
}

const BarraAdicionarItem: React.FC<BarraAdicionarItemProps> = ({ nfeId }) => {
  const { showToast } = useToast();
  const mutation = useAdicionarItemComProduto(nfeId);

  const [produto, setProduto] = useState<BuscarProdutosNFeItem | null>(null);
  const [qty, setQty] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [showPalette, setShowPalette] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const qtyInputRef = useRef<HTMLInputElement>(null);
  const priceInputRef = useRef<HTMLInputElement>(null);
  const searchBtnRef = useRef<HTMLButtonElement>(null);

  const focusProdutoSelecionado = useRef(false);

  const handlePaletteExited = () => {
    if (focusProdutoSelecionado.current) {
      focusProdutoSelecionado.current = false;
      qtyInputRef.current?.focus();
      qtyInputRef.current?.select();
    }
  };

  const handleSelectProduto = (p: BuscarProdutosNFeItem) => {
    focusProdutoSelecionado.current = true;
    setProduto(p);
    setQty('1');
    setUnitPrice(String(p.valor));
    setError(null);
    setShowPalette(false);
  };

  const handleReset = () => {
    setProduto(null);
    setQty('');
    setUnitPrice('');
    setError(null);
    searchBtnRef.current?.focus();
  };

  const handleAdicionar = async () => {
    if (!produto) return;
    setError(null);
    try {
      await mutation.mutateAsync({
        produtoId: produto.id,
        quantidade: Number(qty),
        valorUnitario: Number(unitPrice),
      });
      showToast('Item adicionado', 'success');
      handleReset();
    } catch {
      setError('Erro ao adicionar item. Tente novamente.');
    }
  };

  const semProduto = produto === null;
  const podeSalvar =
    !semProduto &&
    Number(qty) > 0 &&
    Number(unitPrice) > 0 &&
    !mutation.isPending;

  return (
    <>
      <div className="nfe-add-bar">
        {semProduto ? (
          <button
            ref={searchBtnRef}
            type="button"
            className="nfe-add-bar__search--empty"
            onClick={() => setShowPalette(true)}
          >
            <FontAwesomeIcon icon={faSearch} style={{ fontSize: '0.72rem' }} />
            Buscar produto cadastrado...
          </button>
        ) : (
          <div className="nfe-add-bar__produto-selecionado">
            <span className="nfe-add-bar__check">&#x2713;</span>
            <span className="nfe-add-bar__nome" title={produto.descricao}>
              {produto.descricao}
            </span>
            <span className="nfe-add-bar__codigo">{produto.codigo}</span>
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-success"
              style={{ fontSize: '0.7rem', textDecoration: 'none' }}
              onClick={() => setShowPalette(true)}
              disabled={mutation.isPending}
            >
              alterar
            </button>
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-body-secondary ms-1"
              onClick={handleReset}
              disabled={mutation.isPending}
            >
              <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.7rem' }} />
            </button>
          </div>
        )}

        <div className="nfe-add-bar__field">
          <label className="nfe-dest-label">Qtd.</label>
          <input
            ref={qtyInputRef}
            type="number"
            className="form-control form-control-sm text-end"
            style={{ width: 80 }}
            value={qty}
            onChange={e => setQty(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                priceInputRef.current?.focus();
                priceInputRef.current?.select();
              }
            }}
            step="0.0001"
            min="0.0001"
            disabled={semProduto || mutation.isPending}
            placeholder="1"
          />
        </div>

        <div className="nfe-add-bar__field">
          <label className="nfe-dest-label">Vlr. Unit.</label>
          <input
            ref={priceInputRef}
            type="number"
            className="form-control form-control-sm text-end"
            style={{ width: 110 }}
            value={unitPrice}
            onChange={e => setUnitPrice(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && podeSalvar) {
                e.preventDefault();
                handleAdicionar();
              }
            }}
            step="0.0001"
            min="0.0001"
            disabled={semProduto || mutation.isPending}
            placeholder="0,00"
          />
        </div>

        <button
          type="button"
          className="btn btn-primary btn-sm"
          style={{ whiteSpace: 'nowrap' }}
          onClick={handleAdicionar}
          disabled={!podeSalvar}
        >
          {mutation.isPending && (
            <span
              className="spinner-border spinner-border-sm me-1"
              style={{ width: '0.7rem', height: '0.7rem' }}
            />
          )}
          Adicionar
        </button>
      </div>

      {error && (
        <div className="px-3 py-1">
          <small className="text-danger">{error}</small>
        </div>
      )}

      <ModalBuscarProdutoNFe
        show={showPalette}
        onHide={() => setShowPalette(false)}
        onSelecionar={handleSelectProduto}
        onExited={handlePaletteExited}
      />
    </>
  );
};

export default BarraAdicionarItem;
